import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";

// Create GlobalDataContext
const GlobalDataContext = createContext();

// Custom hook to use the context
export const useGlobalData = () => useContext(GlobalDataContext);

export const GlobalDataProvider = ({ children }) => {
  // === Global Data ===
  const [weekStart, setWeekStart] = useState("Monday");
  const [weekEnd, setWeekEnd] = useState("Saturday");
  const [weekends, setWeekend] = useState([0]);
  const [additionalOffDays, setAdditionalOffDays] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [workingHours, setWorkingHours] = useState(9);
  const [relaxation, setRelaxation] = useState(null);
  const [maxRelaxation, setMaxRelaxation] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [dateformat, setDateformat] = useState(null);
  const [businessDate, setBusinessDate] = useState(null);
  const [company, setCompany] = useState(null);

  // === Employee Data ===
  const [employeeData, setEmployeeData] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // === Global States ===
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Cache keys for employee list
  const CACHE_KEY = "employeeData";
  const CACHE_EXPIRY_KEY = "employeeDataExpiry";

  // Helper: get token from localStorage
  const getToken = () => localStorage.getItem("token");

  /**
   * Fetch Global Company Data (if logged in)
   */
  const fetchGlobalData = async (token) => {
    if (!token) return;

    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/company/global-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setWeekStart(data.week_start || "Monday");
      setWeekEnd(data.week_end || "Saturday");
      setWeekend(data.weekend || [0]);
      setAdditionalOffDays(data.additional_off_days || {});
      setHolidays((data.holidays || []).map((h) => h.date));
      setWorkingHours(data.working_hours || 9);
      setRelaxation(data.relaxation);
      setMaxRelaxation(data.max_relaxation);
      setTimezone(data.timezone);
      setCurrency(data.currency);
      setDateformat(data.dateformat);
      setBusinessDate(data.business_date);
      setCompany(data.company);

      // Cache important global data in localStorage
      localStorage.setItem("business_date", data.business_date);
    } catch (error) {
      console.error("Error fetching global data:", error);

      // Handle expired token (401)
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        // optionally redirect user to login
        console.warn("Session expired. Redirecting to login...");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch Employee Data (cached for 3 hours)
   */
  const fetchEmployeeData = async (token) => {
    if (!token) return;

    setEmployeeLoading(true);
    try {
      const now = Date.now();
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cacheExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);

      if (cachedData && cacheExpiry && now < parseInt(cacheExpiry, 10)) {
        // Use cached employee list
        setEmployeeData(JSON.parse(cachedData));
      } else {
        // Fetch new employee list
        const { data } = await axios.get(`${apiUrl}/attendance/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setEmployeeData(data);
        // Cache for 3 hours
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(
          CACHE_EXPIRY_KEY,
          (now + 3 * 60 * 60 * 1000).toString()
        );
      }
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setEmployeeLoading(false);
    }
  };

  /**
   * Trigger API calls only when token is available
   */
  useEffect(() => {
    const token = getToken();
    if (!token) {
      // optional: clear any stale data if no token
      setEmployeeData([]);
      setCompany(null);
      return;
    }

    fetchGlobalData(token);
    fetchEmployeeData(token);
  }, [apiUrl]);

  return (
    <GlobalDataContext.Provider
      value={{
        weekStart,
        weekEnd,
        weekends,
        additionalOffDays,
        holidays,
        workingHours,
        relaxation,
        maxRelaxation,
        timezone,
        currency,
        dateformat,
        businessDate,
        company,
        loading,
        employeeData,
        employeeLoading,
      }}
    >
      {children}
    </GlobalDataContext.Provider>
  );
};
